import React from "react";

import prettyDurations, { DURATION_UNITS } from "../../../../utils/durations";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  dateColumn,
  durationColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { getTransactionUserHref } from "../../transactionAnalytics/tableParams/transactionColumns";

import type { TFunction } from "i18next";

import type { TrialUnsubscriptionRow } from "./trialUnsubscriptionColumns.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface TrialUnsubscriptionColumnsOptions {
  t: TFunction;
  projectSlug: string;
  canAccessUsers: boolean;
}

export { getTransactionUserHref as getTrialUnsubscriptionHref } from "../../transactionAnalytics/tableParams/transactionColumns";

function formatUnsubscriptionDelay(seconds: number): string {
  const { number, string } = prettyDurations(seconds, {
    valueUnit: DURATION_UNITS.SECONDS,
  });
  if (number.days > 0) return string.days;
  if (number.hours > 0) return string.hours;
  return "< 1h";
}

export function trialUnsubscriptionColumns({
  t,
  projectSlug,
  canAccessUsers,
}: TrialUnsubscriptionColumnsOptions): DataTableColumnDef<TrialUnsubscriptionRow>[] {
  return [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      meta: { searchable: true, label: t("name") },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getTransactionUserHref(
            row.original,
            projectSlug,
            canAccessUsers,
          )}
        >
          {row.original.name}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("email")} />
      ),
      meta: { searchable: true, label: t("email") },
      cell: ({ row }) => row.original.email,
    },
    dateColumn<TrialUnsubscriptionRow>(
      "subscriptionDate",
      t("subscriptionDate"),
      { width: "15%", minWidth: 185 },
    ),
    dateColumn<TrialUnsubscriptionRow>(
      "unsubscriptionDate",
      t("unsubscriptionDate"),
      { width: "18%", minWidth: 220 },
    ),
    durationColumn<TrialUnsubscriptionRow>(
      "trialPeriodDuration",
      t("trialPeriodDuration"),
      DURATION_UNITS.SECONDS,
      "humanised",
      { width: "14%", minWidth: 170 },
    ),
    {
      ...durationColumn<TrialUnsubscriptionRow>(
        "subscriptionTrialPeriodDuration",
        t("subscriptionTrialPeriodDuration"),
        DURATION_UNITS.SECONDS,
        "humanised",
        { width: "19%", minWidth: 235 },
      ),
      cell: ({ getValue }) => formatUnsubscriptionDelay(getValue<number>()),
    },
  ];
}
